import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Check, Image as ImageIcon, Plus, Star, Tag, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useXomicsAuth } from './AuthContext';
import {
  createProduct, loadProducts, loadCommerceSettings, loadProductOptions, setProductMainImage, updateProduct,
} from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs, XomicsSelect,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { FilterMenu, isKind, kindOptions } from './ProductFilter';
import ProductOptionsEditor, { optionsSummary } from './ProductOptions';
import ProductImageManager, { photoOptionsOf } from './ProductImageManager';
import ProductPageText, { ShopAddressField } from './ProductPageText';
import ProductReviews from './ProductReviews';
import {
  ImagePicker, NEW_PRODUCT_IMAGES, uploadSelectedImages, useSelectedImages,
} from './ProductImageUpload';
import { useStoreOffice } from './StoreOffice';
import './products.css';
import './collections.css';
import './productDetail.css';

const TYPE_TONE = {
  kit: 'info', supplement: 'ok', wearable: 'warn',
  merch: 'flat', service: 'flat', subscription: 'flat',
};
const TYPE_LABEL_KEY = {
  kit: 'ui.products.type_kit', supplement: 'ui.products.type_supplement',
  wearable: 'ui.products.type_wearable', merch: 'ui.products.type_merchandise',
  service: 'ui.products.type_service', subscription: 'ui.products.type_subscription',
};
const CREATABLE_TYPES = ['supplement', 'wearable', 'merch', 'service', 'subscription'];
const SHIPPABLE_TYPES = new Set(['supplement', 'wearable', 'merch']);

function typeLabel(type) {
  return tx(TYPE_LABEL_KEY[type] || 'ui.products.type_other');
}

// What shipping needs from a physical product: its packed size (all three
// sides or none; the box is picked from it) and, to cross a customs border,
// its HS code and the country it was made in.
function shippingSpecForm(product) {
  const size = product?.dimensionsCm ?? null;
  return {
    lengthCm: size ? String(size.length) : '',
    widthCm: size ? String(size.width) : '',
    heightCm: size ? String(size.height) : '',
    hsCode: product?.hsCode ?? '',
    originCountry: product?.originCountry ?? '',
  };
}

function shippingSpecFields(form) {
  const sides = [form.lengthCm, form.widthCm, form.heightCm].map((value) => String(value).trim());
  return {
    dimensionsCm: sides.every((value) => value === '')
      ? null : { length: Number(sides[0]), width: Number(sides[1]), height: Number(sides[2]) },
    hsCode: form.hsCode.trim() === '' ? null : form.hsCode.trim(),
    originCountry: form.originCountry.trim() === '' ? null : form.originCountry.trim().toUpperCase(),
  };
}

function sizeComplete(form) {
  const sides = [form.lengthCm, form.widthCm, form.heightCm].map((value) => String(value).trim());
  return sides.every((value) => value === '') || sides.every((value) => Number(value) > 0);
}

function ShippingSpecFields({ idPrefix, form, onChange, disabled }) {
  const side = (field, label, aria = {}) => (
    <input {...aria} type="number" min="0.1" step="0.1" inputMode="decimal" aria-label={label}
      placeholder={label} value={form[field]} disabled={disabled}
      onChange={(event) => onChange(field, event.target.value)} />
  );
  return <>
    <div className="xo-product-field-wide">
      <Field id={`${idPrefix}-size`} label={tx('ui.products.packed_size')} hint={tx('ui.products.packed_size_hint')}>
        <span className="xo-product-size-control">
          {side('lengthCm', tx('ui.products.length'), fieldAria(`${idPrefix}-size`, { hint: true }))}
          <span aria-hidden="true">×</span>
          {side('widthCm', tx('ui.products.width'))}
          <span aria-hidden="true">×</span>
          {side('heightCm', tx('ui.products.height'))}
        </span>
      </Field>
    </div>
    <Field id={`${idPrefix}-hs`} label={tx('ui.products.hs_code')} hint={tx('ui.products.hs_code_hint')}>
      <input {...fieldAria(`${idPrefix}-hs`, { hint: true })} inputMode="numeric" maxLength="13"
        value={form.hsCode} disabled={disabled} onChange={(event) => onChange('hsCode', event.target.value)} />
    </Field>
    <Field id={`${idPrefix}-origin`} label={tx('ui.products.made_in')} hint={tx('ui.products.made_in_hint')}>
      <input {...fieldAria(`${idPrefix}-origin`, { hint: true })} maxLength="2" autoCapitalize="characters"
        value={form.originCountry} disabled={disabled}
        onChange={(event) => onChange('originCountry', event.target.value.toUpperCase())} />
    </Field>
  </>;
}

// The product's page on the storefront, from its category and name (XOmics
// works the path out).
function PageAddress({ product }) {
  return <ShopAddressField id={`product-address-${product.id}`} path={product.path} />;
}

// A product's category is a top-level one; its subcategory sits under it.
function topCategories(categories, keepId = null) {
  return categories.filter((category) => category.parentId === null && (category.isActive || category.id === keepId));
}

function subcategoriesOf(categories, categoryId, keepId = null) {
  return categories.filter((category) => category.parentId === Number(categoryId)
    && (category.isActive || category.id === keepId));
}

function SubcategoryField({ id, categories, categoryId, value, keepId, disabled, onChange }) {
  const options = subcategoriesOf(categories, categoryId, keepId);
  return (
    <Field id={id} label={tx('ui.products.subcategory')}>
      <XomicsSelect {...fieldAria(id)} value={value} disabled={disabled || options.length === 0}
        onChange={(event) => onChange(event.target.value)}>
        <option value="">{tx('ui.products.no_subcategory')}</option>
        {options.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
      </XomicsSelect>
    </Field>
  );
}

// A second range the product also shows in on the store. Any subcategory but its own; only the store's range lists
// read it.
function SecondaryCategoryField({ id, categories, subcategoryId, value, keepId, disabled, onChange }) {
  const options = categories.filter((category) => category.parentId !== null
    && (category.isActive || category.id === keepId) && String(category.id) !== subcategoryId);
  return (
    <Field id={id} label={tx('ui.products.secondary_category')}>
      <XomicsSelect {...fieldAria(id)} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value="">{tx('ui.products.no_subcategory')}</option>
        {options.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
      </XomicsSelect>
    </Field>
  );
}

function detailsForm(product) {
  return {
    name: product.name,
    categoryId: String(product.category.id),
    subcategoryId: product.subcategory ? String(product.subcategory.id) : '',
    secondarySubcategoryId: product.secondarySubcategory ? String(product.secondarySubcategory.id) : '',
    shippingWeightGrams: product.shippingWeightGrams === null
      ? '' : String(product.shippingWeightGrams),
    ...shippingSpecForm(product),
  };
}

// What the product is in the catalogue: the same for every office. (What an
// office does with it, its price and its stock, is that office's own, on
// Selling's Products.)
function ProductDetailsEditor({
  product, categories, onSaved, onError, onDirtyChange,
}) {
  const [form, setForm] = useState(() => detailsForm(product));
  const [saving, setSaving] = useState(false);

  const isShippable = product.shippingWeightGrams !== null;
  // Both sides trimmed: a stored value that ends in blank lines is not an edit.
  const stored = detailsForm(product);
  const dirty = Object.keys(stored).some((field) => String(form[field]).trim() !== String(stored[field]).trim());

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  async function submit(event) {
    event.preventDefault();
    const name = form.name.trim();
    const shippingWeightGrams = Number(form.shippingWeightGrams);
    if (name === '') return onError(tx('ui.products.product_name_63c0fe8e'));
    if (isShippable && (!Number.isSafeInteger(shippingWeightGrams) || shippingWeightGrams < 1)) {
      return onError(tx("ui.products.enter_a_positive_whole_number_of_grams_b8e0d340"));
    }
    if (isShippable && !sizeComplete(form)) return onError(tx('ui.products.size_all_or_none'));
    setSaving(true);
    try {
      await updateProduct(product.id, {
        name,
        categoryId: Number(form.categoryId),
        subcategoryId: form.subcategoryId === '' ? null : Number(form.subcategoryId),
        secondarySubcategoryId: form.secondarySubcategoryId === '' ? null : Number(form.secondarySubcategoryId),
        ...(isShippable ? { shippingWeightGrams, ...shippingSpecFields(form) } : {}),
      });
      onSaved(tx("ui.profile.saved_9d4af690"));
    } catch (caught) { onError(errorMessage(caught)); }
    finally { setSaving(false); }
  }

  return (
    <form className="xo-product-details-editor xo-pm-details" onSubmit={submit}>
      <section className="xo-pm-card">
        <h3>{tx('ui.products.detail.listing')}</h3>
        <div className="xo-product-details-grid">
          <Field id={`product-name-${product.id}`} label={tx("ui.products.product_name_63c0fe8e")}>
            <input {...fieldAria(`product-name-${product.id}`)} value={form.name} disabled={saving}
              onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field id={`product-category-${product.id}`} label={tx('ui.products.category_a3c686e7')}>
            <XomicsSelect {...fieldAria(`product-category-${product.id}`)} value={form.categoryId} disabled={saving}
              onChange={(event) => setForm({ ...form, categoryId: event.target.value, subcategoryId: '' })}>
              {topCategories(categories, product.category.id)
                .map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </XomicsSelect>
          </Field>
          <SubcategoryField id={`product-subcategory-${product.id}`} categories={categories}
            categoryId={form.categoryId} value={form.subcategoryId} keepId={product.subcategory?.id ?? null}
            disabled={saving} onChange={(value) => setForm({ ...form, subcategoryId: value })} />
          <SecondaryCategoryField id={`product-secondary-${product.id}`} categories={categories}
            subcategoryId={form.subcategoryId} value={form.secondarySubcategoryId}
            keepId={product.secondarySubcategory?.id ?? null} disabled={saving}
            onChange={(value) => setForm({ ...form, secondarySubcategoryId: value })} />
          <PageAddress product={product} />
        </div>
      </section>
      {isShippable && (
        <section className="xo-pm-card">
          <h3>{tx('ui.store.shipping')}</h3>
          <div className="xo-product-details-grid">
            <Field id={`product-weight-${product.id}`} label={tx("ui.products.packed_weight_ef74a7fd")}
              hint={tx("ui.products.grams_per_unit_69612c19")}>
              <input {...fieldAria(`product-weight-${product.id}`, { hint: true })} type="number" min="1"
                value={form.shippingWeightGrams} disabled={saving}
                onChange={(event) => setForm({ ...form, shippingWeightGrams: event.target.value })} />
            </Field>
            <ShippingSpecFields idPrefix={`product-${product.id}`} form={form} disabled={saving}
              onChange={(field, value) => setForm((current) => ({ ...current, [field]: value }))} />
          </div>
        </section>
      )}
      {dirty && (
        <div className="xo-pm-savebar">
          <span>{tx('ui.products.detail.unsaved')}</span>
          <div>
            <button type="button" className="xo-btn-2" disabled={saving}
              onClick={() => setForm(detailsForm(product))}>{tx('ui.products.detail.discard')}</button>
            <button type="submit" className="xo-btn" disabled={saving}>
              {saving ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

// A new product goes into the catalogue off sale. What an office asks for it
// and how many it has is set afterwards, on that office's Products (Selling).
function NewProduct({ categories, onCreated, onCancel }) {
  const [form, setForm] = useState({
    code: '', name: '', description: '', itemType: 'supplement',
    categoryId: String(topCategories(categories)[0]?.id ?? ''),
    subcategoryId: '',
    shippingWeightGrams: '100',
    ...shippingSpecForm(null),
  });
  const [images, setImages] = useSelectedImages();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const isShippable = SHIPPABLE_TYPES.has(form.itemType);

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [busy, onCancel]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    const weight = Number(form.shippingWeightGrams);
    if (isShippable && (!Number.isSafeInteger(weight) || weight < 1)) { setError(tx("ui.products.packed_weight_must_be_a_positive_whole_number_594679ff")); return; }
    if (isShippable && !sizeComplete(form)) { setError(tx('ui.products.size_all_or_none')); return; }
    if (!Number.isSafeInteger(Number(form.categoryId)) || Number(form.categoryId) < 1) {
      setError(tx('ui.store.error.choose_category'));
      return;
    }

    setBusy(true);
    setProgress(tx("ui.products.creating_product_d78e8e57"));
    let created = null;
    try {
      created = await createProduct({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() === '' ? null : form.description.trim(),
        itemType: form.itemType,
        categoryId: Number(form.categoryId),
        subcategoryId: form.subcategoryId === '' ? null : Number(form.subcategoryId),
        shippingWeightGrams: isShippable ? weight : null,
        ...(isShippable ? shippingSpecFields(form) : {}),
        isActive: false,
      });
      if (images.length > 0) {
        // A new product's photos start in its gallery on the storefront's pages.
        await uploadSelectedImages(created.id, form.name.trim(), images, setProgress, undefined, ['gallery']);
      }
      onCreated({ id: created.id, name: form.name.trim(), warning: null });
    } catch (requestError) {
      const message = errorMessage(requestError);
      if (created !== null) {
        onCreated({ id: created.id, name: form.name.trim(), warning: tx('ui.products.created_but_incomplete', { error: message }) });
      } else {
        setError(message);
        setBusy(false);
        setProgress('');
      }
    }
  }

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={() => { if (!busy) onCancel(); }}>
      <section className="xo-product-editor" role="dialog" aria-modal="true"
        aria-labelledby="new-product-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><h2 id="new-product-title">{tx("ui.products.create_a_product_4ecbb094")}</h2></div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={onCancel}><X aria-hidden="true" /></button>
        </header>
        <form onSubmit={submit} className="xo-product-editor-form">
          <div className="xo-product-editor-scroll">
            <section className="xo-product-form-section">
              <div className="xo-product-form-heading"><div><h3>{tx("ui.products.product_details_0357f902")}</h3></div></div>
              <div className="xo-product-form-grid">
                <Field id="np-code" label={tx("ui.products.sku_d59192b9")}>
                  <input {...fieldAria('np-code')} value={form.code} disabled={busy} maxLength={32}
                    placeholder={tx("ui.products.e_g_sup_vit_d_a7a51141")} onChange={(event) => set('code', event.target.value)} />
                </Field>
                <Field id="np-name" label={tx("ui.products.product_name_63c0fe8e")}>
                  <input {...fieldAria('np-name')} value={form.name} disabled={busy}
                    placeholder={tx("ui.products.vitamin_d3_ae38ed93")} onChange={(event) => set('name', event.target.value)} />
                </Field>
                <Field id="np-type" label={tx("ui.products.type_3deb7456")}>
                  <XomicsSelect {...fieldAria('np-type')} value={form.itemType} disabled={busy}
                    onChange={(event) => set('itemType', event.target.value)}>
                    {CREATABLE_TYPES.map((type) => <option key={type} value={type}>{typeLabel(type)}</option>)}
                  </XomicsSelect>
                </Field>
                <Field id="np-category" label={tx("ui.products.category_a3c686e7")}>
                  <XomicsSelect {...fieldAria('np-category')} value={form.categoryId} disabled={busy}
                    onChange={(event) => setForm((current) => ({ ...current, categoryId: event.target.value, subcategoryId: '' }))}>
                    {topCategories(categories).map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </XomicsSelect>
                </Field>
                <SubcategoryField id="np-subcategory" categories={categories} categoryId={form.categoryId}
                  value={form.subcategoryId} disabled={busy} onChange={(value) => set('subcategoryId', value)} />
                <div className="xo-product-field-wide">
                  <Field id="np-description" label={tx("ui.products.description_55f8ebc8")}>
                    <textarea {...fieldAria('np-description')} rows="3" value={form.description} disabled={busy}
                      placeholder={tx("ui.products.short_catalogue_description_1f145f4b")} onChange={(event) => set('description', event.target.value)} />
                  </Field>
                </div>
              </div>
            </section>

            {isShippable && (
              <section className="xo-product-form-section">
                <div className="xo-product-form-heading"><div><h3>{tx('ui.store.shipping')}</h3></div></div>
                <div className="xo-product-form-grid">
                  <Field id="np-weight" label={tx("ui.products.packed_weight_ef74a7fd")} hint={tx("ui.products.grams_per_unit_69612c19")}>
                    <input {...fieldAria('np-weight', { hint: true })} type="number" min="1"
                      value={form.shippingWeightGrams} disabled={busy}
                      onChange={(event) => set('shippingWeightGrams', event.target.value)} />
                  </Field>
                  <ShippingSpecFields idPrefix="np" form={form} disabled={busy} onChange={set} />
                </div>
              </section>
            )}

            <section className="xo-product-form-section">
              <div className="xo-product-form-heading"><div><h3>{tx("ui.products.product_images_91325e40")}</h3></div></div>
              <ImagePicker images={images} setImages={setImages} disabled={busy} onError={setError}
                limit={NEW_PRODUCT_IMAGES} />
            </section>
            {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
          </div>
          <footer>
            <span>{busy ? progress : tx("ui.products.length_imagevalue_selected_37fc01b5", { length: images.length, value: images.length === 1 ? '' : 's' })}</span>
            <div>
              <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{tx("ui.products.cancel_77dfd213")}</button>
              <button type="submit" className="xo-btn" disabled={busy || form.code.trim() === '' || form.name.trim() === ''}>
                {busy ? tx("ui.products.creating_94d7d8ee") : tx("ui.products.create_product_b1aa5985")}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}

function imageUrlOf(image, size) {
  return image?.variants?.[size]?.url ?? image?.url ?? null;
}

// The product sheet: what the product is in the catalogue, the same for every
// office, one tab per job. Each tab keeps its unsaved edits while another is
// open. What an office does with the product (on sale, stock, prices) is not
// here: that is Selling's Products (XomicsStoreProducts.js), and the sheet's head
// has the way there.
// `mayChooseAny`: the account manages every office's store, and can take the
// product off sale for all of them at once. `onSell`: opens the product where
// an office sells it, null for an account whose place sells nothing (a
// warehouse, a shop), which reads what a product is and nothing of selling it.
function ProductDetail({
  product, products, categories, currencies, mayChooseAny, mayManage, mayEditText, mayReadText,
  onClose, onChanged, onFeedback, onSell,
}) {
  const [tab, setTab] = useState('overview');
  const [selectedImageId, setSelectedImageId] = useState(
    product.images.find((image) => image.isMain)?.id ?? product.images[0]?.id ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [toast, setToast] = useState('');
  // Which photos the Images tab shows: 'all', 'every' (photos for every choice)
  // or one value code of the option photos follow (a finish, a colour).
  const [photoFilter, setPhotoFilter] = useState('all');
  const [optionsState, setOptionsState] = useState({ status: 'loading', document: null, error: '', revision: 0 });
  const [dirty, setDirty] = useState({});
  const [reviewCount, setReviewCount] = useState(null);
  const bodyRef = useRef(null);
  const anyDirty = Object.values(dirty).some(Boolean);
  const currentImage = product.images.find((image) => image.id === selectedImageId)
    ?? product.images.find((image) => image.isMain) ?? product.images[0] ?? null;
  const currentUrl = imageUrlOf(currentImage, 'detail');
  const mainImage = product.images.find((image) => image.isMain) ?? product.images[0] ?? null;
  const headerUrl = imageUrlOf(mainImage, 'thumbnail') ?? product.mainImageCardUrl ?? product.imageUrl ?? null;
  const summary = optionsState.document === null ? null : optionsSummary(optionsState.document);
  const photoOptions = photoOptionsOf(optionsState.document);

  const markDirty = useCallback((part, value) => {
    setDirty((current) => (Boolean(current[part]) === value ? current : { ...current, [part]: value }));
  }, []);
  const detailsDirty = useCallback((value) => markDirty('details', value), [markDirty]);
  const optionsDirty = useCallback((value) => markDirty('options', value), [markDirty]);
  const pageDirty = useCallback((value) => markDirty('page', value), [markDirty]);
  const reviewsDirty = useCallback((value) => markDirty('reviews', value), [markDirty]);

  // The options as the catalogue has them (how many there are of a
  // combination is each office's, in its store).
  const loadOptions = useCallback(async () => {
    try {
      const document = await loadProductOptions(product.id);
      setOptionsState((current) => ({ status: 'ready', document, error: '', revision: current.revision + 1 }));
    } catch (requestError) {
      setOptionsState((current) => ({ ...current, status: 'failed', error: errorMessage(requestError) }));
    }
  }, [product.id]);
  useEffect(() => { loadOptions(); }, [loadOptions]);
  useEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [tab]);

  useEffect(() => {
    if (toast === '') return undefined;
    const timer = window.setTimeout(() => setToast(''), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const close = useCallback(() => {
    if (busy) return;
    if (anyDirty && !window.confirm(tx('ui.products.detail.discard_changes'))) return;
    onClose();
  }, [anyDirty, busy, onClose]);

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [close]);

  function saved(message) {
    setToast(message);
    onFeedback(tx("ui.products.notice_a81b09d9"), message);
    onChanged();
  }

  async function makeMain(image) {
    setBusy(true); setLocalError('');
    try {
      await setProductMainImage(product.id, image.id);
      setSelectedImageId(image.id);
      // By its name: a product's code is internal.
      saved(tx("ui.products.code_main_image_updated_5af768ee", { code: product.name }));
    } catch (requestError) {
      setLocalError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  // Off sale for every office at once: each office's own choice is cleared.
  async function takeOffEverywhere() {
    if (!window.confirm(tx('ui.store.off_everywhere_confirm', { name: product.name }))) return;
    setBusy(true); setLocalError('');
    try {
      await updateProduct(product.id, { isActive: false });
      saved(tx('ui.store.off_everywhere_done', { name: product.name }));
    } catch (requestError) {
      setLocalError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  const tabs = [
    { value: 'overview', label: tx('ui.products.detail.overview') },
    { value: 'options', label: tx('ui.products.detail.options'), count: summary?.count },
    { value: 'images', label: tx("ui.products.images_09e871c9"), count: product.images.length },
    ...(mayReadText ? [{ value: 'page', label: tx('ui.products.page.tab') }] : []),
    ...(onSell ? [{ value: 'reviews', label: tx('ui.products.reviews.tab'), count: reviewCount }] : []),
  ];

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="xo-pm" role="dialog" aria-modal="true"
        aria-labelledby="product-detail-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pm-head">
          <span className="xo-pm-thumb">
            {headerUrl === null ? <ImageIcon aria-hidden="true" /> : <img src={headerUrl} alt="" />}
          </span>
          <div className="xo-pm-title">
            <h2 id="product-detail-title">{product.name}</h2>
            <p>
              <span>{product.category?.name ?? tx("ui.products.uncategorised_10c37342")}</span>
              <Pill tone={TYPE_TONE[product.itemType]}>{typeLabel(product.itemType)}</Pill>
            </p>
          </div>
          <div className="xo-pm-head-actions">
            {/* On sale, price and stock are not here: this is the way to them. */}
            {onSell && (
              <button type="button" className="xo-btn-2 xo-pm-sell" disabled={busy}
                onClick={() => { if (!anyDirty || window.confirm(tx('ui.products.detail.discard_changes'))) onSell(); }}>
                <Tag aria-hidden="true" />{tx('ui.store.price_and_stock')}</button>
            )}
            {mayManage && mayChooseAny && product.isActive && (
              <button type="button" className="xo-btn-2 xo-pm-off-everywhere" disabled={busy} onClick={takeOffEverywhere}>
                {tx('ui.store.off_everywhere')}</button>
            )}
            <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_product_09072853")} onClick={close}>
              <X aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="xo-pm-tabbar">
          <WorkspaceTabs className="xo-pm-tabs" value={tab} onChange={setTab} tabs={tabs}
            label={tx('ui.products.detail.sections')} idPrefix={`product-${product.id}-tab`}
            panelId={`product-${product.id}-panel`} />
        </div>

        <div className="xo-pm-body" ref={bodyRef} id={`product-${product.id}-panel`} role="tabpanel"
          aria-labelledby={`product-${product.id}-tab-${tab}`}>
          {localError && <p className="xo-alert t-bad" role="alert">{localError}</p>}

          <div className="xo-pm-panel xo-pm-overview" hidden={tab !== 'overview'}>
            <div className="xo-pm-gallery">
              <div className="xo-pm-stage">
                {currentUrl === null ? <ImageIcon aria-hidden="true" /> : <img src={currentUrl} alt={currentImage?.altText ?? product.name} />}
              </div>
              {product.images.length > 1 && (
                <div className="xo-pm-thumbs" aria-label={tx("ui.products.product_images_91325e40")}>
                  {product.images.map((image) => (
                    <button type="button" key={image.id} className={image.id === currentImage?.id ? 'is-selected' : ''}
                      aria-label={image.altText || product.name} onClick={() => setSelectedImageId(image.id)}>
                      <img src={imageUrlOf(image, 'thumbnail')} alt="" loading="lazy" decoding="async" />
                      {image.isMain && <Star aria-label={tx("ui.products.main_image_a5fcb218")} />}
                    </button>
                  ))}
                </div>
              )}
              {mayManage && currentImage !== null && !currentImage.isMain && (
                <button type="button" className="xo-link xo-pm-make-main" disabled={busy}
                  onClick={() => makeMain(currentImage)}><Star aria-hidden="true" /> {tx("ui.products.use_as_main_image_56ead489")}</button>
              )}
            </div>

            <div className="xo-pm-overview-main">
              {mayManage ? (
                <ProductDetailsEditor key={`details-${product.updatedAt}`} product={product} categories={categories}
                  onDirtyChange={detailsDirty} onError={setLocalError} onSaved={saved} />
              ) : (
                <div className="xo-pm-card">
                  <div className="xo-product-details-editor"><PageAddress product={product} /></div>
                  <dl className="xo-product-specs">
                    <div><dt>{tx("ui.products.shipping_weight_9f02d2af")}</dt><dd>{product.shippingWeightGrams === null ? tx("ui.products.not_shipped_5baeb6b8") : tx("ui.products.value_g_79d2b4ba", { value: product.shippingWeightGrams.toLocaleString() })}</dd></div>
                    {product.shippingWeightGrams !== null && <>
                      <div><dt>{tx('ui.products.packed_size')}</dt><dd>{product.dimensionsCm ? `${product.dimensionsCm.length} × ${product.dimensionsCm.width} × ${product.dimensionsCm.height}` : tx('ui.products.not_set')}</dd></div>
                      <div><dt>{tx('ui.products.hs_code')}</dt><dd>{product.hsCode ?? tx('ui.products.not_set')}</dd></div>
                      <div><dt>{tx('ui.products.made_in')}</dt><dd>{product.originCountry ?? tx('ui.products.not_set')}</dd></div>
                    </>}
                  </dl>
                </div>
              )}
            </div>
          </div>

          <div className="xo-pm-panel" hidden={tab !== 'options'}>
            {optionsState.document === null ? (
              optionsState.status === 'failed'
                ? <Failed message={optionsState.error} onRetry={loadOptions} />
                : <Loading label={tx('ui.products.options.loading')} />
            ) : (
              <ProductOptionsEditor key={optionsState.revision} product={product} currencies={currencies}
                document={optionsState.document} mayManage={mayManage} onDirtyChange={optionsDirty}
                photoOption={photoOptions[0]?.code ?? null}
                onManagePhotos={(valueCode) => { setPhotoFilter(valueCode); setTab('images'); }}
                onSaved={(document) => {
                  setOptionsState((current) => ({ status: 'ready', document, error: '', revision: current.revision + 1 }));
                  saved(tx('ui.products.options.saved', { code: product.name }));
                }} />
            )}
          </div>

          <div className="xo-pm-panel" hidden={tab !== 'images'}>
            <ProductImageManager product={product} photoOptions={photoOptions} mayManage={mayManage}
              filter={photoFilter} onFilter={setPhotoFilter} onSaved={saved}
              onOpen={(imageId) => { setSelectedImageId(imageId); setTab('overview'); }} />
          </div>

          {mayReadText && (
            <div className="xo-pm-panel" hidden={tab !== 'page'}>
              <ProductPageText key={`page-${product.id}-${product.updatedAt}`} product={product} products={products}
                mayEdit={mayEditText && mayManage} mayEditProduct={mayManage} onDirtyChange={pageDirty} onSaved={saved}
                onOpenImages={() => setTab('images')} />
            </div>
          )}

          {onSell && (
            <div className="xo-pm-panel" hidden={tab !== 'reviews'}>
              <ProductReviews product={product} mayManage={mayManage} onCount={setReviewCount}
                onDirtyChange={reviewsDirty} />
            </div>
          )}
        </div>

        {toast && <p className="xo-pm-toast" role="status"><Check aria-hidden="true" />{toast}</p>}
      </section>
    </div>
  );
}

// One product of the catalogue: its picture, filling the card, what it is
// called and where it belongs. The same for every office.
function ProductCard({ product, onOpen }) {
  const imageUrl = product.mainImageCardUrl ?? product.imageUrl ?? null;
  return (
    <button type="button" className="xo-pc-card" onClick={onOpen}>
      <span className="xo-pc-card-art">
        {imageUrl === null
          ? <ImageIcon aria-hidden="true" />
          : <img src={imageUrl} alt="" loading="lazy" decoding="async" />}
      </span>
      <span className="xo-pc-card-body">
        <strong>{product.name}</strong>
        <span className="xo-pc-card-category">{product.category?.name || tx("ui.products.uncategorised_10c37342")}</span>
      </span>
    </button>
  );
}

// The catalogue's products: what they are. Names, pictures, pages, options.
// The same for every office, and the only place a product is added (its
// categories have their own page beside it, XomicsCategories.js). What an
// office sells of the catalogue, at what price and how many it has, is that
// office's own: Selling's Products (XomicsStoreProducts.js), its own item in the
// sidebar.
export default function XomicsProducts() {
  const { permissions, user } = useXomicsAuth();
  const mayManage = permissions.includes('PRODUCT_MANAGE');
  // A warehouse and a shop sell nothing: their people read what a product is, not how it is sold.
  const sells = !['warehouse', 'retail'].includes(user?.location?.kind) || permissions.includes('STORE_MANAGE_ALL');
  const navigate = useNavigate();
  // Read for one thing: whether this account may take a product off sale for every office.
  const store = useStoreOffice();
  const list = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  const loadSettings = useCallback(() => loadCommerceSettings(), []);
  const settingsResource = useLiveResource(['products'], loadSettings);
  const [query, setQuery] = useState('');
  // Every product, or one kind of them.
  const [kind, setKind] = useState('all');
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');
  // The product just added: it is off sale until an office prices it.
  const [added, setAdded] = useState(null);

  const products = useMemo(() => list.data ?? [], [list.data]);
  const settings = settingsResource.data ?? {
    categories: [], currencies: [], countries: [], languages: [],
  };
  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return products.filter((product) => isKind(product, kind) && (needle === ''
      || `${product.name} ${product.category?.name ?? ''} ${product.code}`.toLowerCase().includes(needle)));
  }, [kind, products, query]);
  const selected = products.find((product) => product.id === selectedId) ?? null;

  if ((list.loading && list.data === null) || (settingsResource.loading && settingsResource.data === null)) {
    return <Loading label={tx("ui.products.reading_the_catalogue_2a05727e")} />;
  }
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  if (settingsResource.error) {
    return <Failed message={settingsResource.error} onRetry={settingsResource.refresh} />;
  }

  function feedback(kind, message) {
    setAdded(null);
    setNotice(kind === 'notice' ? message : '');
    setWarning(kind === 'warning' ? message : '');
  }

  function created({ id, name, warning: createdWarning }) {
    setAdding(false);
    setNotice('');
    setWarning(createdWarning ?? '');
    setAdded({ id, name });
    list.refresh();
  }

  // Where an office sells a product: its form on Selling's Products.
  const sell = (id) => navigate(`/xomics/store/products?product=${id}`);

  return (
    <div className="xo-products-page xo-pc-page">
      <Head title={tx('ui.nav.product_information')} subtitle={tx('ui.store.subtitle.product_information')}>
        {mayManage && (
          <button type="button" className="xo-btn" onClick={() => { setAdding(true); feedback('', ''); }}>
            <Plus aria-hidden="true" />{tx("ui.products.new_product_1bc965f3")}</button>
        )}
      </Head>

      {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
      {warning && <p className="xo-alert t-warn" role="alert">{warning}</p>}
      {/* A new product is in the catalogue, off sale: the way on is the office's own page. */}
      {added !== null && (
        <p className="xo-alert t-ok xo-pc-added" role="status">
          <span><Check aria-hidden="true" />{tx('ui.store.added', { name: added.name })}</span>
          <button type="button" className="xo-link" onClick={() => sell(added.id)}>{tx('ui.store.set_price_and_stock')}</button>
        </p>
      )}

      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          <SearchField className="xo-product-search" value={query} onChange={setQuery} placeholder={tx("ui.products.search_products_4412bfe5")} />
          <div className="xo-product-toolbar-actions">
            <FilterMenu value={kind} onChange={setKind} options={kindOptions(products)} label={tx('ui.store.filter')} />
          </div>
        </div>
      </div>
      {found.length === 0
        ? <Empty title={tx("ui.products.nothing_matches_17e7aebf")} />
        : <div className="xo-pc-grid">
          {found.map((product) => <ProductCard key={product.id} product={product} onOpen={() => setSelectedId(product.id)} />)}
        </div>}

      {adding && <NewProduct categories={settings.categories} onCancel={() => setAdding(false)} onCreated={created} />}
      {selected !== null && <ProductDetail product={selected} products={products} categories={settings.categories}
        currencies={settings.currencies} mayChooseAny={store.canChooseAny} mayManage={mayManage} onSell={sells ? () => sell(selected.id) : null}
        mayEditText={permissions.includes('CONTENT_MANAGE')} mayReadText={permissions.includes('CONTENT_READ')}
        onClose={() => setSelectedId(null)} onChanged={list.refresh} onFeedback={feedback} />}
    </div>
  );
}
